import 'bootstrap/js/dist/button';
import 'bootstrap/js/dist/collapse';
import 'bootstrap/js/dist/dropdown';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import 'prismjs';
import './index.css';
import './utils/dayjs-setup';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Router, matchPath } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { store } from './store';
import * as RoutesModule from './routes';
import * as Sentry from "@sentry/react";
import {createBrowserHistory} from 'history';
import SentryErrorBoundary from './components/SentryErrorBoundary';
import { TitleProvider } from './utils/title';
import { version } from './version';
import TrimbleMaps from "@trimblemaps/trimblemaps-js";
import { ThemeSwitcherProvider } from "react-css-theme-switcher";
import { AlertProvider } from 'components/useAlert';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const themes = {
  dark: `/css/dark-theme.css?v=${version}`,
  light: `/css/light-theme.css?v=${version}`,
};
const defaultTheme = localStorage.getItem("darkmode") === "false" ? "light" : "dark";

const history = createBrowserHistory();

const routes: { path: string }[] = Object.entries(RoutesModule.paths).map(x => { return { path: x[1] }});

// Configure Sentry
Sentry.init({
  release: `loadone@${version}`,
  environment: window.location.hostname === "loadboard-staging.azurewebsites.net" ? "staging" : undefined,
  enabled: window.location.hostname !== "localhost",
  dsn: "https://406b2b1377cb40afad3672ae4b6eeb5c@o261143.ingest.sentry.io/1825006",
  integrations: [
    Sentry.reactRouterV5BrowserTracingIntegration({
      history,
      routes,
      matchPath
    }),
  ],

  // Only sample .5% of sessions
  tracesSampleRate: 0.005,
});

// Configure TrimbleMaps
TrimbleMaps.setAPIKey("87A1A88FC4D1F14CB0FAC557C2C792FF");

// Configure isTMS
window.isTMS = true;

// Configure React Query
const queryClient = new QueryClient();

// Handle when the user navigates to a page that has not been preloaded
// while using the previous version of the app.
// https://github.com/vitejs/vite/issues/11804
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
})

function renderApp() {
  const container = document.getElementById('react-app');
  const root = createRoot(container);
  root.render(
    <SentryErrorBoundary>
      <Provider store={store}>
        <TitleProvider title="Load One LLC">
          <AlertProvider>
            <ThemeSwitcherProvider
              themeMap={themes}
              defaultTheme={defaultTheme}
              insertionPoint={document.getElementById('inject-styles-here')}
            >
              <QueryClientProvider client={queryClient}>
                <Router history={history}>
                  <CompatRouter children={RoutesModule.routes} />
                </Router>
              </QueryClientProvider>
            </ThemeSwitcherProvider>
          </AlertProvider>
        </TitleProvider>
      </Provider>
    </SentryErrorBoundary>
  );
}

renderApp();
